import { Head, router } from "@inertiajs/react"

import TextLink from "@/components/text-link"
import AttributeDisplay from "@/components/ui/attribute-display"
import AttributesContainer from "@/components/ui/attributes-container"
import LogoPreview from "@/components/ui/logo-preview"
import RecordPageHeader from "@/components/ui/record-page-header"
import type { RecordPageHeaderAction } from "@/components/ui/record-page-header"
import AdminLayout from "@/layouts/admin-layout"
import { formatRelativeTime } from "@/lib/utils"
import {
  adminPartnerPath,
  adminPartnerUserPath,
  adminPartnerUsersPath,
  editAdminPartnerUserPath,
  impersonateAdminPartnerUserPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface PartnerUser {
  uid: string
  name: string
  email: string
  role: string
  created_at: string
  invitation_accepted_at: string | null
  last_sign_in_at: string | null
  avatar_url: string | null
  partner: { name: string; uid: string } | null
}

export default function ShowPartnerUser({
  partner_user,
}: {
  partner_user: PartnerUser
}) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Partner Users", href: adminPartnerUsersPath() },
    {
      title: partner_user.name || partner_user.email,
      href: adminPartnerUserPath({ id: partner_user.uid }),
    },
  ]

  const status = partner_user.invitation_accepted_at ? "active" : "invited"

  // Only offer impersonation once the partner user has a real, usable session
  // (i.e. they've accepted their invitation).
  const actions: RecordPageHeaderAction[] = partner_user.invitation_accepted_at
    ? [
        {
          label: "Impersonate",
          onClick: () =>
            router.post(
              impersonateAdminPartnerUserPath({ id: partner_user.uid }),
            ),
          inline: true,
        },
      ]
    : []

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={partner_user.name || partner_user.email} />
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-6 p-4">
        <RecordPageHeader
          title={partner_user.name || partner_user.email}
          state={status}
          editPath={editAdminPartnerUserPath({ id: partner_user.uid })}
          deletePath={
            partner_user.invitation_accepted_at
              ? undefined
              : adminPartnerUserPath({ id: partner_user.uid })
          }
          actions={actions}
        />
        <div className="space-y-3">
          <h2 className="text-base font-semibold">Details</h2>
          <AttributesContainer className="gap-6">
            <AttributeDisplay label="Email" value={partner_user.email} />
            <AttributeDisplay
              label="Partner"
              value={
                partner_user.partner ? (
                  <TextLink
                    href={adminPartnerPath({ id: partner_user.partner.uid })}
                    as="div"
                    prefetch
                  >
                    {partner_user.partner.name}
                  </TextLink>
                ) : (
                  "-"
                )
              }
            />
            <AttributeDisplay label="Role" value={partner_user.role} />
            <AttributeDisplay
              label="Last sign in"
              value={formatRelativeTime(
                partner_user.last_sign_in_at ?? undefined,
              )}
            />
            {partner_user.avatar_url && (
              <AttributeDisplay
                label="Photo"
                value={
                  <LogoPreview
                    src={partner_user.avatar_url}
                    alt={partner_user.name}
                    className="w-16"
                  />
                }
              />
            )}
          </AttributesContainer>
        </div>
      </div>
    </AdminLayout>
  )
}
